import { Animation, LayerType, modTypes } from "@blings/blings-player";
import { LayerInspectorSelection } from "./layerInspectorTools";
import { isInteractiveMod } from "./modTypeGuards";
import { isParagraphLayerSelectable } from "./layerSelectorFilters";

type PrefillableModSnapshot = Partial<{
  type: modTypes;
  layerName: string;
  layerUid: number;
  assetId: string;
  additionals?: Array<{
    assetId?: string;
    layerName?: string;
    layerUid?: number;
  }>;
  imageName: string;
  isLayers: boolean;
}>;

type PrefillLayerArgs = {
  snapshot: PrefillableModSnapshot;
  isAssetChange: boolean;
  selection?: LayerInspectorSelection;
  selections?: LayerInspectorSelection[];
  jsonFile?: Animation;
  fallbackAssetId?: string;
};

type ResolveInspectorPrefillPrimarySelectionArgs = {
  primarySelection?: LayerInspectorSelection;
  explicitSelections?: LayerInspectorSelection[];
  contextMenuLayer?: LayerInspectorSelection;
};

const MEDIA_LAYER_TYPES = new Set<LayerType>([
  LayerType.still,
  LayerType.video,
  LayerType.imageSeq,
]);

const COLOR_LAYER_TYPES = new Set<LayerType>([
  LayerType.text,
  LayerType.shape,
  LayerType.solid,
  LayerType.precomp,
]);

const BASIC_LAYER_BASED_MODS = new Set<modTypes>([
  modTypes.hideLayer,
  modTypes.custom,
  modTypes.transform,
  modTypes.color,
  modTypes.embedElement,
]);

export function resolveInspectorPrefillPrimarySelection({
  primarySelection,
  explicitSelections,
  contextMenuLayer,
}: ResolveInspectorPrefillPrimarySelectionArgs):
  | LayerInspectorSelection
  | undefined {
  if (contextMenuLayer) return contextMenuLayer;
  if (explicitSelections?.length) {
    return explicitSelections[explicitSelections.length - 1];
  }
  return primarySelection;
}

export function prefillLayerFromInspector({
  snapshot,
  isAssetChange,
  selection,
  selections,
  jsonFile,
  fallbackAssetId = "main",
}: PrefillLayerArgs): void {
  if (!snapshot || typeof snapshot !== "object") return;

  const type = snapshot.type;
  if (!type) return;
  if (snapshot.layerName) return;
  if (!shouldAttemptPrefill(type)) return;

  const selectionList = selections?.length
    ? selections
    : selection
    ? [selection]
    : [];
  const primarySelection = selection ?? selectionList[selectionList.length - 1];

  if (!primarySelection?.layerName || !jsonFile) return;

  if (isAssetChange) {
    const resolvedAsset = resolveAssetDetails(primarySelection, jsonFile);
    if (resolvedAsset) snapshot.imageName = resolvedAsset.imageName;
    return;
  }

  const resolvedLayer = resolveLayerDetails(
    primarySelection,
    jsonFile,
    fallbackAssetId
  );
  if (!resolvedLayer) return;

  const matchesType =
    type === modTypes.interactiveInput
      ? isParagraphLayerSelectable({
          layerName: resolvedLayer.layerName,
          assetId: resolvedLayer.assetId,
          layerUid: resolvedLayer.layerUid,
          json: jsonFile,
          fallbackAssetId,
        })
      : selectionMatchesMod(type, resolvedLayer);
  if (!matchesType) return;

  snapshot.layerName = resolvedLayer.layerName;
  if (resolvedLayer.assetId) {
    snapshot.assetId =
      resolvedLayer.assetId === fallbackAssetId
        ? undefined
        : resolvedLayer.assetId;
  } else if (fallbackAssetId && snapshot.assetId === undefined) {
    snapshot.assetId = undefined;
  }
  if (typeof resolvedLayer.layerUid === "number") {
    snapshot.layerUid = resolvedLayer.layerUid;
  }

  if (type === modTypes.themeColor) {
    snapshot.isLayers = true;
  }

  if (selectionList.length > 1) {
    const primaryKey = `${resolvedLayer.assetId ?? fallbackAssetId}::${
      typeof resolvedLayer.layerUid === "number" ? resolvedLayer.layerUid : ""
    }::${resolvedLayer.layerName}`;
    const seen = new Set<string>();
    const additionals = selectionList
      .map((entry) => {
        const resolved = resolveLayerDetails(entry, jsonFile, fallbackAssetId);
        if (!resolved) return null;
        const matchesAdditional =
          type === modTypes.interactiveInput
            ? isParagraphLayerSelectable({
                layerName: resolved.layerName,
                assetId: resolved.assetId,
                layerUid: resolved.layerUid,
                json: jsonFile,
                fallbackAssetId,
              })
            : selectionMatchesMod(type, resolved);
        if (!matchesAdditional) return null;
        const assetId =
          resolved.assetId === fallbackAssetId ? undefined : resolved.assetId;
        const layerUidKey =
          typeof resolved.layerUid === "number" ? resolved.layerUid : "";
        const key = `${assetId ?? fallbackAssetId}::${layerUidKey}::${
          resolved.layerName
        }`;
        if (key === primaryKey) return null;
        if (seen.has(key)) return null;
        seen.add(key);
        return {
          assetId,
          layerName: resolved.layerName,
          layerUid: resolved.layerUid,
        };
      })
      .filter((entry): entry is {
        assetId?: string;
        layerName: string;
        layerUid?: number;
      } => Boolean(entry));

    if (additionals.length) {
      snapshot.additionals = additionals;
    }
  }
}

export function resolveSelectionLayerDetails(
  selection: LayerInspectorSelection | undefined,
  jsonFile: Animation | undefined,
  fallbackAssetKey: string
): ResolvedLayerDetails | null {
  if (!selection || !jsonFile) return null;
  return resolveLayerDetails(selection, jsonFile, fallbackAssetKey);
}

function shouldAttemptPrefill(type: modTypes): boolean {
  if (
    type === modTypes.text ||
    type === modTypes.asset ||
    type === modTypes.themeColor
  ) {
    return true;
  }
  if (isInteractiveMod(type)) return true;
  return BASIC_LAYER_BASED_MODS.has(type);
}

type ResolvedLayerDetails = {
  assetId: string;
  layerName: string;
  layerUid?: number;
  layerType?: LayerType;
};

export type ResolvedAssetDetails = {
  imageName: string | undefined;
};

type AnimationLayerEntry = Animation["layers"][number];

type LayerResolutionResult = {
  layer: AnimationLayerEntry;
  location: "root" | "asset";
  assetId?: string;
};

function getAssetSelectorFromClassName(
  className: string | undefined
): string | undefined {
  return className?.match(/\$a_(.*?)_\$l_/)?.[1];
}

/**
 * Resolves the actual animation layer referenced by the inspector selection.
 * The inspector wraps the entire scene in a fake pre-comp when concat utility is active.
 * Those fake pre-comps do not have an asset uid, while real pre-comps do. We use that
 * heuristic to decide whether to search the root scene layers or the assets collection
 * and fall back to the opposite if the first attempt fails.
 */
export function resolveLayerFromSelection(
  selection: LayerInspectorSelection,
  jsonFile: Animation
): LayerResolutionResult | null {
  const assets = jsonFile.assets ?? [];
  const hasRealAsset = typeof selection.assetUid === "number";

  const resolveLayerFromPath = (
    path: string | undefined
  ): LayerResolutionResult | null => {
    if (!path) return null;
    const assetMatch = path.match(/^assets\[(\d+)\]\.layers\[(\d+)\]/);
    if (assetMatch) {
      const assetIndex = Number(assetMatch[1]);
      const layerIndex = Number(assetMatch[2]);
      if (!Number.isFinite(assetIndex) || !Number.isFinite(layerIndex)) {
        return null;
      }
      const asset = assets[assetIndex];
      const layer = asset?.layers?.[layerIndex];
      if (!layer) return null;
      const assetKey =
        typeof asset?.nm === "string" && asset.nm.length > 0
          ? asset.nm
          : asset?.id;
      return assetKey
        ? { layer, location: "asset", assetId: assetKey }
        : { layer, location: "asset" };
    }

    const rootMatch = path.match(/^layers\[(\d+)\]/);
    if (rootMatch) {
      const layerIndex = Number(rootMatch[1]);
      if (!Number.isFinite(layerIndex)) return null;
      const layer = jsonFile.layers?.[layerIndex];
      if (!layer) return null;
      return { layer, location: "root" };
    }

    return null;
  };

  const resolvedFromPath = resolveLayerFromPath(selection.path);
  if (resolvedFromPath) {
    const pathLayerUid = resolvedFromPath.layer.uid;
    if (
      typeof selection.layerUid !== "number" ||
      pathLayerUid === selection.layerUid
    ) {
      return resolvedFromPath;
    }
  }

  if (typeof selection.layerUid !== "number") return null;
  const layerUid = selection.layerUid;

  const findLayerInAssets = (
    assetList: typeof assets,
    preferredAssetNamesOrIds: Array<string | undefined> = []
  ): LayerResolutionResult | null => {
    if (!assetList.length) return null;
    const preferredAssets = preferredAssetNamesOrIds.filter(
      (entry): entry is string => typeof entry === "string" && entry.length > 0
    );
    const matchesPreferredAsset = (asset: typeof assets[number]) =>
      preferredAssets.some((preferredAsset) => {
        return asset.id === preferredAsset || asset.nm === preferredAsset;
      });
    const orderedAssets = preferredAssets.length
      ? [
          ...assetList.filter(matchesPreferredAsset),
          ...assetList.filter((asset) => !matchesPreferredAsset(asset)),
        ]
      : assetList;
    for (const asset of orderedAssets) {
      const assetLayers = Array.isArray(asset.layers) ? asset.layers : [];
      const targetLayer = assetLayers.find((entry) => entry.uid === layerUid);
      if (targetLayer) {
        const assetKey =
          typeof asset.nm === "string" && asset.nm.length > 0
            ? asset.nm
            : asset.id;
        return { layer: targetLayer, location: "asset", assetId: assetKey };
      }
    }
    return null;
  };

  if (!hasRealAsset) {
    const layer = jsonFile.layers.find((entry) => entry.uid === layerUid);
    if (layer) {
      return { layer, location: "root" };
    }
  }

  const layerInsideAssets = findLayerInAssets(assets, [
    selection.assetName,
    getAssetSelectorFromClassName(selection.className),
  ]);
  if (layerInsideAssets) {
    return layerInsideAssets;
  }

  if (hasRealAsset) {
    const layer = jsonFile.layers.find((entry) => entry.uid === layerUid);
    if (layer) {
      return { layer, location: "root" };
    }
  }

  return null;
}

export function resolveAssetDetails(
  selection: LayerInspectorSelection,
  jsonFile: Animation
): ResolvedAssetDetails | null {
  const resolvedLayer = resolveLayerFromSelection(selection, jsonFile);
  if (!resolvedLayer) return null;

  const targetLayer = resolvedLayer.layer as AnimationLayerEntry & {
    refId?: string;
  };
  if (!("refId" in targetLayer) || typeof targetLayer.refId !== "string") {
    return null;
  }

  const asset = jsonFile.assets?.find(
    (entry) => entry.id === targetLayer.refId
  );
  if (!asset || !asset.p) return null;

  return { imageName: asset.p };
}

export function resolveLayerDetails(
  selection: LayerInspectorSelection,
  jsonFile: Animation,
  fallbackAssetKey: string
): ResolvedLayerDetails | null {
  const resolvedLayer = resolveLayerFromSelection(selection, jsonFile);
  if (!resolvedLayer) return null;

  const { layer, location, assetId: resolvedAssetId } = resolvedLayer;
  if (!layer) return null;

  const layerName =
    typeof layer.nm === "string" && layer.nm.length > 0
      ? layer.nm
      : selection.layerName;
  if (!layerName) return null;
  const assetId =
    resolvedAssetId ??
    (location === "asset"
      ? selection.assetName ?? fallbackAssetKey
      : fallbackAssetKey);

  return {
    assetId,
    layerName,
    layerUid:
      typeof layer.uid === "number"
        ? layer.uid
        : selection.layerUid ?? layer.uid,
    layerType:
      typeof layer.ty === "number" ? (layer.ty as LayerType) : undefined,
  };
}

function selectionMatchesMod(
  type: modTypes,
  resolved: ResolvedLayerDetails
): boolean {
  switch (type) {
    case modTypes.text:
      return resolved.layerType === LayerType.text;
    case modTypes.asset:
      return !!resolved.layerType && MEDIA_LAYER_TYPES.has(resolved.layerType);
    case modTypes.themeColor:
    case modTypes.color:
      return !!resolved.layerType && COLOR_LAYER_TYPES.has(resolved.layerType);
    default:
      if (isInteractiveMod(type)) return true;
      return BASIC_LAYER_BASED_MODS.has(type);
  }
}
